"use client";

import {
  auth,
  createPhoenixClient,
  MemoryAuthSessionStorage,
  type PhoenixClient,
} from "@ellipsis-labs/rise";
import { base64 } from "@scure/base";
import { getBase58Decoder } from "@solana/kit";
import { Transaction } from "@solana/web3.js";

import { getApiUrl } from "@/lib/config";
import { getRiseClient } from "@/lib/rise/client";

const AUTHORITY_STORAGE_KEY = "phoenix-flight-auth-authority";
const ACCESS_REFRESH_WINDOW_MS = 60_000;

type WalletAuthClient = {
  auth: NonNullable<PhoenixClient["auth"]>;
  sessionManager: NonNullable<PhoenixClient["sessionManager"]>;
  dispose: PhoenixClient["dispose"];
};

export interface SessionWalletSigner {
  walletName?: string;
  signMessage?: (message: Uint8Array) => Promise<Uint8Array>;
  signTransaction?: (transaction: Transaction) => Promise<Transaction>;
}

const pendingSessions = new Map<string, Promise<auth.AuthSession>>();

const createWalletAuthClient = (): WalletAuthClient => {
  const sessionManager = new auth.AuthSessionManager(
    new MemoryAuthSessionStorage()
  );
  const client = createPhoenixClient({
    baseUrl: getApiUrl(),
    auth: true,
    authConfig: {
      sessionManager,
      sessionControl: "external",
    },
  });

  if (client.auth === undefined || client.sessionManager === undefined) {
    throw new Error("Failed to initialize rise auth client");
  }

  return {
    auth: client.auth,
    sessionManager: client.sessionManager,
    dispose: () => client.dispose(),
  };
};

const readStoredAuthority = (): string | null => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(AUTHORITY_STORAGE_KEY);
};

const writeStoredAuthority = (authority: string): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AUTHORITY_STORAGE_KEY, authority);
};

const clearStoredAuthority = (): void => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTHORITY_STORAGE_KEY);
};

const shouldRefreshSession = (session: auth.AuthSession): boolean => {
  if (auth.isRefreshExpired(session)) return false;
  return auth.isAccessExpiringWithin(session, ACCESS_REFRESH_WINDOW_MS);
};

const canReuseSession = (
  session: auth.AuthSession | null,
  authority: string
): session is auth.AuthSession => {
  if (session === null) return false;
  if (readStoredAuthority() !== authority) return false;
  return !auth.isAccessExpired(session);
};

const refreshSession = async (
  session: auth.AuthSession,
  client: PhoenixClient
): Promise<auth.AuthSession> => {
  if (!shouldRefreshSession(session)) return session;
  const authClient = client.auth;
  if (authClient === undefined || client.sessionManager === undefined) {
    return session;
  }
  return client.sessionManager.refreshWith((refreshToken) =>
    authClient.refresh(refreshToken)
  );
};

const getAuthorizedRiseSession = async (
  forceRefresh = false
): Promise<auth.AuthSession | null> => {
  const client = getRiseClient();
  const sessionManager = client.sessionManager;
  if (sessionManager === undefined) return null;

  let session = await sessionManager.getSession();
  if (session === null) return null;

  if (auth.isRefreshExpired(session)) {
    await sessionManager.clearSession();
    return null;
  }

  if (forceRefresh || shouldRefreshSession(session)) {
    const authClient = client.auth;
    if (authClient === undefined) {
      return auth.isAccessExpired(session) ? null : session;
    }

    try {
      session = await sessionManager.refreshWith((refreshToken) =>
        authClient.refresh(refreshToken)
      );
    } catch {
      await sessionManager.clearSession();
      return null;
    }
  }

  return auth.isAccessExpired(session) ? null : session;
};

export const getRiseAuthorizationHeader = async ({
  authority,
  forceRefresh = false,
}: {
  authority?: string;
  forceRefresh?: boolean;
} = {}): Promise<string | null> => {
  if (authority !== undefined && readStoredAuthority() !== authority) {
    return null;
  }

  const session = await getAuthorizedRiseSession(forceRefresh);
  return session === null ? null : `Bearer ${session.accessToken}`;
};

class WalletSignMessageError extends Error {
  override readonly name = "WalletSignMessageError";

  constructor(readonly cause: unknown) {
    super(
      cause instanceof Error
        ? cause.message
        : `Wallet signMessage failed: ${String(cause)}`
    );
  }
}

const signWithWalletMessage = async (
  authority: string,
  signer: SessionWalletSigner
): Promise<auth.AuthSession> => {
  if (signer.signMessage === undefined) {
    throw new WalletSignMessageError(
      new Error("Wallet does not support message signing")
    );
  }

  const authClient = createWalletAuthClient();
  try {
    const nonce = await authClient.auth.getWalletNonce(authority);
    const messageBytes = new TextEncoder().encode(nonce.message);
    let signatureBytes: Uint8Array;
    try {
      signatureBytes = await signer.signMessage(messageBytes);
    } catch (error) {
      throw new WalletSignMessageError(error);
    }
    const signature = getBase58Decoder().decode(signatureBytes);

    return authClient.auth.loginWithWalletSignature(
      authority,
      signature,
      nonce.nonce_id
    );
  } finally {
    authClient.dispose();
  }
};

const signWithMemoTransaction = async (
  authority: string,
  signer: SessionWalletSigner
): Promise<auth.AuthSession> => {
  if (signer.signTransaction === undefined) {
    throw new Error("This wallet does not support transaction signing.");
  }

  const authClient = createWalletAuthClient();
  try {
    const challenge =
      await authClient.auth.getWalletTransactionChallenge(authority);
    const unsignedTransaction = Transaction.from(
      base64.decode(challenge.unsigned_transaction)
    );
    const signedTransaction = await signer.signTransaction(unsignedTransaction);
    const signedTransactionBase64 = base64.encode(
      new Uint8Array(
        signedTransaction.serialize({
          requireAllSignatures: false,
          verifySignatures: false,
        })
      )
    );

    return authClient.auth.loginWithWalletTransaction(
      authority,
      signedTransactionBase64,
      challenge.nonce_id
    );
  } finally {
    authClient.dispose();
  }
};

const signForSession = async (
  authority: string,
  signer: SessionWalletSigner
): Promise<auth.AuthSession> => {
  auth.debugAuth("wallet session bootstrap start", {
    pubkey: authority,
    walletName: signer.walletName,
  });

  try {
    let session: auth.AuthSession;
    const shouldSkipMessageSigning =
      signer.signMessage === undefined ||
      signer.walletName?.toLowerCase().includes("ledger") === true;

    if (shouldSkipMessageSigning) {
      auth.debugAuth("using wallet transaction auth", {
        pubkey: authority,
        walletName: signer.walletName,
      });
      session = await signWithMemoTransaction(authority, signer);
    } else {
      try {
        session = await signWithWalletMessage(authority, signer);
      } catch (error) {
        if (!(error instanceof WalletSignMessageError)) {
          throw error;
        }
        if (signer.signTransaction === undefined) {
          throw error.cause instanceof Error ? error.cause : error;
        }
        auth.debugAuth(
          "wallet message signing rejected, falling back to transaction auth",
          {
            pubkey: authority,
            walletName: signer.walletName,
            error:
              error.cause instanceof Error
                ? { name: error.cause.name, message: error.cause.message }
                : { value: String(error.cause) },
          }
        );
        session = await signWithMemoTransaction(authority, signer);
      }
    }

    auth.debugAuth("wallet session bootstrap success", {
      pubkey: authority,
      sessionId: session.sessionId,
      accessExpiresAt: session.expiresAt,
      refreshExpiresAt: session.refreshExpiresAt,
    });

    return session;
  } catch (error) {
    auth.debugAuth("wallet session bootstrap failed", {
      pubkey: authority,
      walletName: signer.walletName,
      error:
        error instanceof Error
          ? { name: error.name, message: error.message }
          : { value: String(error) },
    });
    throw error;
  }
};

const ensureRiseSessionInternal = async (
  authority: string,
  signer: SessionWalletSigner
): Promise<auth.AuthSession> => {
  const client = getRiseClient();
  if (client.sessionManager === undefined) {
    throw new Error("Rise auth session manager is unavailable");
  }

  let session = await client.sessionManager.getSession();
  if (readStoredAuthority() !== authority && session !== null) {
    await client.sessionManager.clearSession();
    session = null;
  }

  if (canReuseSession(session, authority)) {
    return refreshSession(session, client);
  }

  if (
    session !== null &&
    readStoredAuthority() === authority &&
    !auth.isRefreshExpired(session)
  ) {
    const authClient = client.auth;
    try {
      if (authClient === undefined) return session;
      return await client.sessionManager.refreshWith((refreshToken) =>
        authClient.refresh(refreshToken)
      );
    } catch {
      await client.sessionManager.clearSession();
    }
  }

  const nextSession = await signForSession(authority, signer);
  await client.sessionManager.importSnapshot(auth.toSnapshot(nextSession));
  writeStoredAuthority(authority);
  return nextSession;
};

export const ensureRiseSession = (
  authority: string,
  signer: SessionWalletSigner
): Promise<auth.AuthSession> => {
  const pending = pendingSessions.get(authority);
  if (pending !== undefined) return pending;

  const run = ensureRiseSessionInternal(authority, signer).finally(() => {
    if (pendingSessions.get(authority) === run) {
      pendingSessions.delete(authority);
    }
  });

  pendingSessions.set(authority, run);
  return run;
};

export const clearRiseSession = async (): Promise<void> => {
  clearStoredAuthority();
  const client = getRiseClient();
  try {
    await client.auth?.logout();
  } catch {
    await client.sessionManager?.clearSession();
  }
  await client.sessionManager?.clearSession();
};
